import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { fetchBooks, deleteBook, updateBook } from "../../store/slices/booksSlice";
import Loader from "../../../components/UtilityComponents/Loader";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../../styles/common.css";

const AdminBooks = () => {
  const dispatch = useDispatch();
  const { books, loading, error } = useSelector((state) => state.books);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedBook, setSelectedBook] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    title_long: "",
    authors: "",
    description: "",
    price: "",
    isbn_10: "",
    isbn_13: "",
    language: "",
    number_of_pages: "",
    number_of_ratings: "",
    publish_date: "",
    publisher: "",
    rating: "",
    status: "1",
    image_url: "",
  });

  useEffect(() => {
    dispatch(fetchBooks());
  }, [dispatch]);

  const handleDelete = async (book) => {
    setSelectedBook(book);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await dispatch(deleteBook(selectedBook.id)).unwrap();
      setDeleteDialogOpen(false);
      toast.success('Book deleted successfully');
    } catch (error) {
      console.error("Failed to delete book:", error);
      toast.error('Failed to delete book');
    }
  };

  const handleEditClick = (book) => {
    setSelectedBook(book);
    setFormData({
      title: book.title || "",
      title_long: book.title_long || "",
      authors: book.authors || "",
      description: book.description || "",
      price: book.price || "",
      isbn_10: book.isbn_10 || "",
      isbn_13: book.isbn_13 || "",
      language: book.language || "",
      number_of_pages: book.number_of_pages || "",
      number_of_ratings: book.number_of_ratings || "",
      publish_date: book.publish_date || "",
      publisher: book.publisher || "",
      rating: book.rating || "",
      status: book.status?.toString() || "1",
      image_url: book.image_url || "",
    });
    setEditDialogOpen(true);
  };

  const handleViewClick = (book) => {
    setSelectedBook(book);
    setViewDialogOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    try {
      await dispatch(updateBook({ bookId: selectedBook.id, data: formData })).unwrap();
      setEditDialogOpen(false);
      toast.success('Book updated successfully');
    } catch (error) {
      console.error("Failed to update book:", error);
      toast.error('Failed to update book');
    }
  };

  const filteredBooks = books?.filter((book) =>
    book?.title?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  if (loading) return <Loader />;
  if (error) return <div className="alert alert-danger">{error}</div>;

  return (
    <div className="container-fluid">
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="row mb-4">
        <div className="col-md-6">
          <h1 className="h3">Books Management</h1>
        </div>
        <div className="col-md-6 text-end">
          <Link to="/admin/books/add" className="btn btn-save">
            Add New Book
          </Link>
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-md-4">
          <input
            type="text"
            className="form-control"
            placeholder="Search books..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Author</th>
                  <th>Price</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredBooks?.map((book) => (
                  <tr key={book.id}>
                    <td>{book.title}</td>
                    <td>{book.authors}</td>
                    <td>${book.price}</td>
                    <td>
                      <span className={`status-badge ${book.status === "1" ? "active" : "inactive"}`}>
                        {book.status === "1" ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn btn-sm btn-view me-2"
                        onClick={() => handleViewClick(book)}
                      >
                        View
                      </button>
                      <button
                        className="btn btn-sm btn-edit me-2"
                        onClick={() => handleEditClick(book)}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-sm btn-delete"
                        onClick={() => handleDelete(book)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteDialogOpen && (
        <div className="modal">
          <div className="modal-content">
            <h3>Confirm Delete</h3>
            <p>Are you sure you want to delete "{selectedBook?.title}"?</p>
            <div className="modal-actions">
              <button className="btn btn-view" onClick={() => setDeleteDialogOpen(false)}>Cancel</button>
              <button className="btn btn-delete" onClick={handleDeleteConfirm}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Book Modal */}
      {viewDialogOpen && selectedBook && (
        <div className="modal">
          <div className="modal-content">
            <h3>Book Details</h3>
            <div className="book-info">
              <div className="info-group">
                <label>Title:</label>
                <span>{selectedBook.title}</span>
              </div>
              <div className="info-group">
                <label>Long Title:</label>
                <span>{selectedBook.title_long}</span>
              </div>
              <div className="info-group">
                <label>Authors:</label>
                <span>{selectedBook.authors}</span>
              </div>
              <div className="info-group">
                <label>Description:</label>
                <span>{selectedBook.description}</span>
              </div>
              <div className="info-group">
                <label>Price:</label>
                <span>${selectedBook.price}</span>
              </div>
              <div className="info-group">
                <label>ISBN-10:</label>
                <span>{selectedBook.isbn_10}</span>
              </div>
              <div className="info-group">
                <label>ISBN-13:</label>
                <span>{selectedBook.isbn_13}</span>
              </div>
              <div className="info-group">
                <label>Language:</label>
                <span>{selectedBook.language}</span>
              </div>
              <div className="info-group">
                <label>Number of Pages:</label>
                <span>{selectedBook.number_of_pages}</span>
              </div>
              <div className="info-group">
                <label>Number of Ratings:</label>
                <span>{selectedBook.number_of_ratings}</span>
              </div>
              <div className="info-group">
                <label>Publish Date:</label>
                <span>{selectedBook.publish_date}</span>
              </div>
              <div className="info-group">
                <label>Publisher:</label>
                <span>{selectedBook.publisher}</span>
              </div>
              <div className="info-group">
                <label>Rating:</label>
                <span>{selectedBook.rating}</span>
              </div>
              <div className="info-group">
                <label>Status:</label>
                <span className={`status-badge ${selectedBook.status === "1" ? "active" : "inactive"}`}>
                  {selectedBook.status === "1" ? "Active" : "Inactive"}
                </span>
              </div>
              {selectedBook.image_url && (
                <div className="info-group">
                  <label>Image:</label>
                  <img src={selectedBook.image_url} alt={selectedBook.title} className="book-image" />
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button className="btn btn-view" onClick={() => setViewDialogOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Book Modal */}
      {editDialogOpen && selectedBook && (
        <div className="modal">
          <div className="modal-content">
            <h3>Edit Book</h3>
            <form onSubmit={handleUpdateSubmit}>
              <div className="form-group">
                <label>Title</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Long Title</label>
                <input
                  type="text"
                  name="title_long"
                  value={formData.title_long}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label>Authors</label>
                <input
                  type="text"
                  name="authors"
                  value={formData.authors}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                />
              </div>
              <div className="form-group">
                <label>Price</label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  step="0.01"
                  required
                />
              </div>
              <div className="form-group">
                <label>ISBN-10</label>
                <input
                  type="text"
                  name="isbn_10"
                  value={formData.isbn_10}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label>ISBN-13</label>
                <input
                  type="text"
                  name="isbn_13"
                  value={formData.isbn_13}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label>Language</label>
                <input
                  type="text"
                  name="language"
                  value={formData.language}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label>Number of Pages</label>
                <input
                  type="number"
                  name="number_of_pages"
                  value={formData.number_of_pages}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label>Number of Ratings</label>
                <input
                  type="number"
                  name="number_of_ratings"
                  value={formData.number_of_ratings}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label>Publish Date</label>
                <input
                  type="text"
                  name="publish_date"
                  value={formData.publish_date}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label>Publisher</label>
                <input
                  type="text"
                  name="publisher"
                  value={formData.publisher}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label>Rating</label>
                <input
                  type="number"
                  name="rating"
                  value={formData.rating}
                  onChange={handleInputChange}
                  step="0.1"
                />
              </div>
              <div className="form-group">
                <label>Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  required
                >
                  <option value="1">Active</option>
                  <option value="0">Inactive</option>
                </select>
              </div>
              <div className="form-group">
                <label>Image URL</label>
                <input
                  type="text"
                  name="image_url"
                  value={formData.image_url}
                  onChange={handleInputChange}
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-view" onClick={() => setEditDialogOpen(false)}>Cancel</button>
                <button type="submit" className="btn btn-save">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBooks;